/**
 * SplitView — Master-detail layout.
 *
 * Wide (>= breakpoint): side-by-side panels.
 * Narrow (< breakpoint): shows master OR detail (not both).
 *   - When detail is null → shows master
 *   - When detail is provided → shows detail with onBack header
 *
 * Uses useWindowDimensions for reactive resize handling.
 */

import React from 'react';
import { View, TouchableOpacity, Text, useWindowDimensions } from 'react-native';
import { useThemeColors, colors } from '../../theme';

interface SplitViewProps {
  /** Left panel */
  master: React.ReactNode;
  /** Right panel (null = nothing selected) */
  detail: React.ReactNode | null;
  /** Placeholder shown when detail is null on wide screens */
  detailPlaceholder?: React.ReactNode;
  /** Width threshold to activate side-by-side mode (default: 1024) */
  breakpoint?: number;
  /** Fixed master panel width in pixels (default: 360) */
  masterWidth?: number;
  /** Label for the back button in narrow mode (default: 'Back') */
  backLabel?: string;
  /** Called when back is pressed in narrow detail mode */
  onBack?: () => void;
}

export function SplitView({
  master,
  detail,
  detailPlaceholder,
  breakpoint = 1024,
  masterWidth = 360,
  backLabel = '← Back',
  onBack,
}: SplitViewProps) {
  const { width } = useWindowDimensions();
  const theme = useThemeColors();
  const isSplit = width >= breakpoint;

  // ─── Wide: side-by-side ───
  if (isSplit) {
    return (
      <View style={{ flex: 1, flexDirection: 'row' }}>
        <View style={{ width: masterWidth }}>
          {master}
        </View>
        <View style={{ width: 1, backgroundColor: theme.border }} />
        <View style={{ flex: 1 }}>
          {detail ?? detailPlaceholder ?? null}
        </View>
      </View>
    );
  }

  // ─── Narrow with detail selected: show detail with back ───
  if (detail && onBack) {
    return (
      <View style={{ flex: 1 }}>
        <TouchableOpacity
          onPress={onBack}
          accessibilityRole="button"
          accessibilityLabel="Back"
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: 16,
            paddingVertical: 12,
            borderBottomWidth: 1,
            borderBottomColor: theme.border,
            backgroundColor: theme.card,
          }}
        >
          <Text style={{ fontSize: 16, color: colors.primary, fontWeight: '600' }}>
            {backLabel}
          </Text>
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          {detail}
        </View>
      </View>
    );
  }

  // ─── Narrow without detail: show master ───
  return <>{master}</>;
}
